import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCheckCircle, FaClock, FaExclamationTriangle } from "react-icons/fa";
import api from "../services/api";
import "../styles/booking-processing.css";

const BookingProcessing = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [timeLeft, setTimeLeft] = useState(120);
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    const processBooking = async () => {
      try {
        console.log("📋 Booking ID from URL:", bookingId);

        if (!bookingId) {
          console.error("❌ No booking ID");
          setStatus("timeout");
          return;
        }

        // Get booking data from localStorage
        let bookingData = null;
        try {
          const stored =
            localStorage.getItem("bookingConfirmation") ||
            sessionStorage.getItem("bookingConfirmation");
          if (stored) {
            bookingData = JSON.parse(stored);
            console.log(
              "✅ Found booking data in storage:",
              bookingData.bookingId
            );
          }
        } catch (e) {
          console.warn("Could not parse stored booking data");
        }

        // If we have booking data and session_id from Stripe, confirm the booking
        const sessionId = searchParams.get("session_id");
        if (bookingData && sessionId) {
          console.log("📋 Confirming booking with payment confirmation...");
          try {
            const confirmResponse = await api.post("/payment/confirm-booking", {
              bookingId: bookingData.bookingId,
              showId: bookingData.showId,
              seats: bookingData.seats,
              totalAmount: bookingData.totalAmount,
              userEmail: bookingData.userEmail,
              movieTitle: bookingData.movieTitle,
            });

            if (confirmResponse.data.success) {
              console.log("✅ Booking confirmed successfully");
              setStatus("completed");
              localStorage.removeItem("bookingConfirmation");
              sessionStorage.removeItem("bookingConfirmation");
              setTimeout(() => {
                navigate(`/booking-confirmation/${bookingId}`);
              }, 1500);
              return;
            }
          } catch (confirmError) {
            console.error(
              "❌ Booking confirmation error:",
              confirmError.message
            );
          }
        }

        // Fallback: Retry fetching booking from backend
        let response = null;
        let attempts = 0;
        const maxAttempts = 5;
        const baseDelay = 500;

        while (attempts < maxAttempts && !response) {
          try {
            console.log(
              `📋 Fetching booking attempt ${attempts + 1}/${maxAttempts}...`
            );
            response = await api.get(`/bookings/${bookingId}`);

            if (response.data) {
              console.log("✅ Booking found from backend");
              setStatus("completed");
              localStorage.removeItem("bookingConfirmation");
              sessionStorage.removeItem("bookingConfirmation");
              setTimeout(() => {
                navigate(`/booking-confirmation/${bookingId}`);
              }, 1500);
              return;
            }
          } catch (error) {
            attempts++;
            if (attempts < maxAttempts) {
              const delay = baseDelay * Math.pow(2, attempts - 1);
              console.log(`⏳ Retry in ${delay}ms...`);
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }
        }

        // If backend fetch failed but we have localStorage data, use it
        if (bookingData && bookingData.bookingId === bookingId) {
          console.log("✅ Using booking data from storage");
          setStatus("completed");
          localStorage.removeItem("bookingConfirmation");
          sessionStorage.removeItem("bookingConfirmation");
          setTimeout(() => {
            navigate(`/booking-confirmation/${bookingId}`);
          }, 1500);
          return;
        }

        console.error("❌ Could not retrieve booking after retries");
        setStatus("timeout");
      } catch (error) {
        console.error("❌ Booking processing error:", error.message);
        setStatus("timeout");
      }
    };

    processBooking();

    const timerInterval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          if (status === "processing") {
            setStatus("timeout");
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [bookingId, navigate, searchParams]);

  const progress = ((120 - timeLeft) / 120) * 100;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = (timeLeft % 60).toString().padStart(2, "0");

  return (
    <div className="booking-processing-container">
      <motion.div
        className="processing-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="status-icon-wrapper"
          animate={status === "processing" ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {status === "processing" && (
            <div className="loader-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
          )}
          {status === "completed" && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <FaCheckCircle className="success-icon" />
            </motion.div>
          )}
          {status === "timeout" && (
            <FaExclamationTriangle className="error-icon" />
          )}
        </motion.div>

        <motion.h1
          className="processing-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {status === "processing" && "Processing Your Booking"}
          {status === "completed" && "Booking Confirmed!"}
          {status === "timeout" && "Confirmation Timeout"}
        </motion.h1>

        <motion.p
          className="processing-description"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {status === "processing" &&
            "Your payment is being confirmed. Please wait..."}
          {status === "completed" && "Your booking has been confirmed!"}
          {status === "timeout" && "Please refresh and try again."}
        </motion.p>

        <motion.div
          className="progress-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="progress-bar-wrapper">
            <div
              className="progress-bar-fill"
              style={{
                width: `${progress}%`,
                backgroundColor:
                  status === "completed"
                    ? "#10b981"
                    : status === "timeout"
                    ? "#ef4444"
                    : "#e50914",
              }}
            >
              <div className="progress-shimmer"></div>
            </div>
          </div>
          <div className="progress-text">
            <span className="progress-percentage">{Math.round(progress)}%</span>
          </div>
        </motion.div>

        <motion.div
          className="timer-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="timer-icon">
            <FaClock />
          </div>
          <div className="timer-display">
            <span className="timer-value">
              {minutes}:{seconds}
            </span>
            <span className="timer-label">seconds remaining</span>
          </div>
        </motion.div>

        <motion.div
          className="booking-id-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <p className="booking-id-label">Booking ID</p>
          <p className="booking-id-value">{bookingId}</p>
        </motion.div>

        <motion.div
          className="status-steps"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div
            className={`step ${
              status !== "processing" ? "completed" : "active"
            }`}
          >
            <div className="step-number">1</div>
            <span className="step-label">Payment</span>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${status === "completed" ? "completed" : ""}`}>
            <div className="step-number">2</div>
            <span className="step-label">Confirmation</span>
          </div>
          <div className="step-divider"></div>
          <div className="step">
            <div className="step-number">3</div>
            <span className="step-label">Tickets</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default BookingProcessing;
